import React from 'react';
import styled from 'styled-components';
import { fmtTime, rangeDateLabel } from 'TimeOffList/services/format';
import StaffAvatar from 'components/StaffAvatar';
import { IconCalendar, IconX, IconCheckSquare } from 'TimeOffList/components/icons';
import type { IStagedRecord } from '../services/staged-records';

interface Props {
  records: IStagedRecord[];
  indexById: Record<number, number>;
  avatarById: Record<number, string>;
  creating: boolean;
  onRemove: (id: number) => void;
  onCancel: () => void;
  onCreate: () => void;
}

const SelectedPanel: React.FC<Props> = ({ records, indexById, avatarById, creating, onRemove, onCancel, onCreate }) => {
  const count = records.length;
  const canCreate = count > 0 && !creating;

  return (
    <Panel>
      <Head>
        <SectionLabel>Selected</SectionLabel>
        <span className="count">{count}</span>
      </Head>

      <List>
        {count === 0 ? (
          <Empty>
            <IconCheckSquare size={34} strokeWidth={1.6} />
            <span>Nothing added yet. Fill the form and press Add.</span>
          </Empty>
        ) : (
          records.map(r => (
            <RecordCard key={r.id}>
              <div className="top">
                <StaffAvatar name={r.name} avatar={avatarById[r.staffId]} gradIndex={indexById[r.staffId]} size={34} fontSize={12} />
                <span className="name">{r.name}</span>
                <RemoveBtn onClick={() => onRemove(r.id)} title="Remove">
                  <IconX size={15} strokeWidth={2.4} />
                </RemoveBtn>
              </div>
              <div className="date">
                <IconCalendar size={13} color="var(--fg-4)" />
                <span>{rangeDateLabel(r.start, r.end)}</span>
              </div>
              <div className="bottom">
                <TimePill $allDay={r.allDay}>
                  {r.allDay ? 'All day' : `${fmtTime(r.fromMin || 0)} – ${fmtTime(r.toMin || 0)}`}
                </TimePill>
                <span className="reason">{r.reason || '—'}</span>
              </div>
            </RecordCard>
          ))
        )}
      </List>

      <Footer>
        <CancelBtn onClick={onCancel}>Cancel</CancelBtn>
        <CreateBtn onClick={onCreate} disabled={!canCreate} $enabled={canCreate}>
          {creating ? 'Creating…' : `Create${count > 0 ? ` (${count})` : ''}`}
        </CreateBtn>
      </Footer>
    </Panel>
  );
};

export default SelectedPanel;

const Panel = styled.div`
  width: 300px;
  flex-shrink: 0;
  background: var(--bg-app);
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Head = styled.div`
  padding: 16px 20px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .count { font-size: 12px; font-weight: 700; color: #fff; background: var(--acc); border-radius: 999px; padding: 2px 9px; }
`;

const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 44px 16px;
  color: var(--fg-4);
  text-align: center;
  span { font-size: 13px; }
`;

const RecordCard = styled.div`
  padding: 11px 12px;
  border-radius: var(--r-lg);
  background: var(--bg-surface);
  border: 1px solid var(--border-1);
  display: flex;
  flex-direction: column;
  gap: 7px;
  .top { display: flex; align-items: center; gap: 9px; }
  .top .name { flex: 1; min-width: 0; font-size: 14px; font-weight: 700; color: var(--fg-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .date { display: flex; align-items: center; gap: 6px; padding-left: 2px; font-size: 12.5px; font-weight: 600; color: var(--fg-2); }
  .date svg { flex-shrink: 0; }
  .date span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bottom { display: flex; align-items: center; gap: 8px; padding-left: 2px; min-width: 0; }
  .bottom .reason { min-width: 0; font-size: 12.5px; color: var(--fg-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const RemoveBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: var(--fg-4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { background: var(--danger-100); color: var(--danger); }
`;

const TimePill = styled.span<{ $allDay: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;
  background: ${p => (p.$allDay ? 'var(--brand-navy)' : 'var(--acc-soft)')};
  color: ${p => (p.$allDay ? '#fff' : 'var(--acc)')};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--border-1);
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 12px 0;
  border-radius: var(--r-md);
  border: 1px solid var(--border-1);
  background: var(--bg-surface);
  color: var(--fg-2);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); }
`;

const CreateBtn = styled.button<{ $enabled: boolean }>`
  flex: 1;
  padding: 12px 0;
  border-radius: var(--r-md);
  border: none;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: inherit;
  color: #fff;
  background: ${p => (p.$enabled ? 'var(--acc)' : 'var(--acc-disabled)')};
  cursor: ${p => (p.$enabled ? 'pointer' : 'not-allowed')};
  box-shadow: ${p => (p.$enabled ? 'var(--shadow-sm)' : 'none')};
`;
